import React, { useState } from 'react';
import Modal from 'react-modal';
import { X, Plus, Minus, RefreshCw, Save } from 'lucide-react';
import { useStock } from '../hooks/useStock';
import useStockStore from '../store/stockStore';

const TYPE_OPTIONS = [
  { value: 'in',         label: 'Stok Girişi',    icon: Plus,       color: 'text-green-600 bg-green-50 border-green-300' },
  { value: 'out',        label: 'Stok Çıkışı',    icon: Minus,      color: 'text-red-600 bg-red-50 border-red-300' },
  { value: 'adjustment', label: 'Düzeltme',        icon: RefreshCw,  color: 'text-blue-600 bg-blue-50 border-blue-300' },
];

export function StockAdjustModal({ onDone }) {
  const { adjustingProduct, closeAdjust } = useStockStore();
  const { saving, applyMovement } = useStock();
  const [type, setType] = useState('in');
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');

  if (!adjustingProduct) return null;

  const handleSave = async () => {
    const q = parseInt(qty);
    if (!q || q <= 0) { alert('Geçerli bir miktar girin'); return; }
    try {
      const newQty = await applyMovement({
        serviceId:   adjustingProduct.id,
        serviceName: adjustingProduct.name,
        currentQty:  adjustingProduct.stockQty ?? 0,
        type,
        qty:         q,
        note,
      });
      onDone?.(adjustingProduct.id, newQty);
      closeAdjust();
    } catch (err) {
      alert('Stok güncellenemedi: ' + err.message);
    }
  };

  const handleClose = () => {
    setType('in');
    setQty('');
    setNote('');
    closeAdjust();
  };

  return (
    <Modal
      isOpen={!!adjustingProduct}
      onRequestClose={handleClose}
      style={{
        overlay: {
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
        },
        content: {
          position: 'relative',
          top: 'auto', left: 'auto', right: 'auto', bottom: 'auto',
          maxWidth: '400px',
          width: '90%',
          padding: 0,
          border: 'none',
          background: 'transparent',
          overflow: 'visible',
        },
      }}
    >
      <div className="bg-white rounded-lg shadow-xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4 flex justify-between items-center rounded-t-lg">
          <h2 className="text-lg font-semibold text-white">Stok Güncelle</h2>
          <button onClick={handleClose} className="text-white hover:text-gray-200 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Ürün bilgisi */}
          <div className="bg-gray-50 rounded-lg px-4 py-3">
            <p className="text-sm font-medium text-gray-900">{adjustingProduct.name}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Mevcut stok: <span className="font-semibold text-gray-800">{adjustingProduct.stockQty ?? 0} {adjustingProduct.unit || 'adet'}</span>
            </p>
          </div>

          {/* İşlem tipi */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">İşlem Tipi</label>
            <div className="grid grid-cols-3 gap-2">
              {TYPE_OPTIONS.map(opt => {
                const Icon = opt.icon;
                const selected = type === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setType(opt.value)}
                    className={`flex flex-col items-center gap-1 border rounded-lg py-2.5 px-2 text-xs font-medium transition-all ${
                      selected ? opt.color + ' border-2' : 'text-gray-500 bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Miktar */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Miktar <span className="text-gray-400 font-normal">({adjustingProduct.unit || 'adet'})</span>
            </label>
            <input
              type="number"
              min="1"
              value={qty}
              onChange={e => setQty(e.target.value)}
              placeholder="0"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Not */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Not <span className="text-xs text-gray-400 font-normal">(opsiyonel)</span>
            </label>
            <input
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Ör: Tedarikçiden alındı, bozuldu…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-5 py-4 flex justify-end gap-3 rounded-b-lg">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          >
            İptal
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-md transition-all disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
